import { useState, useEffect, useMemo } from 'react';
import { useUserContext } from '../contexts/UserContext';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Monitor, CreditCard, Zap, Users, Trophy } from 'lucide-react';
import PlanShowcaseModal from '../components/PlanShowcaseModal';

export interface MembershipPlan {
  id: string;
  name: string;
  description: string | null;
  durationMonths: number | null;
  price: number | null;
  isActive: boolean;
  features?: any;
  gym: {
    id: string;
    name: string;
    displayName: string;
  };
  _count?: {
    memberships: number;
  };
}

export default function PlansPreviewPage() {
  const { userContext } = useUserContext();
  const [plans, setPlans] = useState<MembershipPlan[]>([]);
  const [gymName, setGymName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('No access token');

      const response = await fetch(`${API_BASE_URL}/api/member-plans`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const plansData = await response.json();
        const activePlans = (plansData.data || []).filter((p: MembershipPlan) => p.isActive);
        setPlans(activePlans);

        if (activePlans.length > 0 && activePlans[0].gym) {
          setGymName(activePlans[0].gym.displayName || activePlans[0].gym.name);
        } else if (userContext?.roles && userContext.roles.length > 0) {
          setGymName(userContext.roles[0].gymName || 'Gym');
        }
      }
    } catch (error) {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    const totalPlans = plans.length;
    const activePlans = plans.filter(p => p.isActive).length;
    const totalMembers = plans.reduce((sum, plan) => sum + (plan._count?.memberships || 0), 0);
    const mostPopularPlan = plans.length > 0
      ? plans.reduce((max, plan) => (plan._count?.memberships || 0) > (max._count?.memberships || 0) ? plan : max)
      : null;

    return { totalPlans, activePlans, totalMembers, mostPopularPlan };
  }, [plans]);

  const [isShowcaseOpen, setIsShowcaseOpen] = useState(false);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-8">
      <div className="max-w-7xl mx-auto space-y-12">
        {/* Navigation & Action Bar */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="group flex items-center gap-2 px-5 py-2.5 bg-white text-gray-600 rounded-2xl font-black text-xs uppercase tracking-widest border border-gray-100 hover:border-blue-200 transition-all active:scale-95 shadow-sm"
          >
            <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to Dashboard
          </button>

          <button
            onClick={() => setIsShowcaseOpen(true)}
            className="flex items-center gap-3 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-lg shadow-blue-100 transition-all active:scale-95 group"
          >
            <Monitor className="w-4 h-4 group-hover:scale-110 transition-transform" />
            Showcase Mode
          </button>
        </div>

        {/* Hero Section */}
        <div className="text-center relative py-12">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 via-purple-600/5 to-pink-600/5 rounded-[4rem] blur-3xl -z-10"></div>
          <h1 className="text-4xl sm:text-6xl font-black text-gray-900 mb-6 tracking-tight">
            {gymName || 'Gym'} <span className="text-blue-600">Plans</span>
          </h1>
          <p className="text-lg sm:text-xl font-bold text-gray-400 uppercase tracking-[0.3em]">Signature Membership catalog</p>
        </div>

        {/* Dynamic Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {[
            { label: 'Total Tiers', value: stats.totalPlans, icon: <CreditCard className="w-5 h-5 text-blue-600" />, color: 'blue' },
            { label: 'Live Plans', value: stats.activePlans, icon: <Zap className="w-5 h-5 text-amber-500" />, color: 'amber' },
            { label: 'Subscribers', value: stats.totalMembers, icon: <Users className="w-5 h-5 text-indigo-600" />, color: 'indigo' },
            { label: 'Top Seller', value: stats.mostPopularPlan?.name || 'N/A', icon: <Trophy className="w-5 h-5 text-emerald-600" />, color: 'emerald' }
          ].map((stat, i) => (
            <div key={i} className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden group hover:border-blue-100 transition-colors">
              <div className="relative">
                <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center mb-4">
                  {stat.icon}
                </div>
                <div className="text-2xl font-black text-gray-900 truncate">{stat.value}</div>
                <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {plans.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-500 text-lg">No active plans available</p>
          </div>
        )}

        {/* Note/Help text */}
        <div className="bg-blue-50 border border-blue-100 rounded-3xl p-8 text-center max-w-2xl mx-auto">
          <p className="text-blue-900 font-bold mb-2">Looking for More?</p>
          <p className="text-sm text-blue-700/70 font-medium">Click on Showcase Mode to view a full-screen presentable version for your members.</p>
        </div>
      </div>

      <PlanShowcaseModal
        isOpen={isShowcaseOpen}
        onClose={() => setIsShowcaseOpen(false)}
        plans={plans}
        gymName={gymName}
      />
    </div>
  );
}
